import { useTableTemplateStore } from "../../store/tableTemplateStore";
import type { TableColumn } from "../../models/ManualAuthoring";
import { findColumnById } from "./tableBuilderUtils/columnUtils";

export default function ColumnEditor() {
  const schema = useTableTemplateStore((s) => s.editorSchema);
  const selected = useTableTemplateStore((s) => s.selectedColumnId);
  const updateColumn = useTableTemplateStore((s) => s.updateColumn);
  const addChildColumn = useTableTemplateStore((s) => s.addChildColumn);
  const deleteColumn = useTableTemplateStore((s) => s.deleteColumn);
  const selectColumn = useTableTemplateStore((s) => s.selectColumn);
  const lockState = useTableTemplateStore((s) => s.lockState);

  if (!schema || !selected) {
    return <div className="panel-empty">Select a column to edit its properties.</div>;
  }

  const column = findColumnById(schema.columns, selected);
  if (!column) return <div className="panel-empty">Selected column no longer exists.</div>;

  const isLocked = !!lockState?.isLocked;

  const setField = <K extends keyof TableColumn>(field: K, value: TableColumn[K]) => {
    updateColumn(column.id, { [field]: value } as Partial<TableColumn>);
  };

  const widthValue = column.width != null ? String(column.width) : "";
  const resolvedAlign: NonNullable<TableColumn["align"]> = column.align ?? "left";

  return (
    <section className="column-editor-card" aria-label="Column Properties">
      <div className="column-editor-header">
        <div className="column-editor-title-row">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M6.66667 3.33333H2M8 12.6667H2M9.33333 2V4.66667M10.6667 11.3333V14M14 8H8M14 12.6667H10.6667M14 3.33333H9.33333M5.33333 6.66667V9.33333M5.33333 8H2"
              stroke="#003366"
              strokeWidth="1.33333"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <h3 className="column-editor-title">Column Properties</h3>
        </div>
        <p className="column-editor-subtitle">
          Selected column: {column.header || "Untitled Column"}. Edit publishing
          properties that affect PDF layout and backend mapping.
        </p>
      </div>

      <div className="column-editor-body">
        <div className="column-form-grid">
          <div className="column-form-field column-form-field-full">
            <label htmlFor="col-header">Header</label>
            <input
              id="col-header"
              value={column.header}
              onChange={(e) => setField("header", e.target.value)}
              disabled={isLocked}
            />
          </div>

          <div className="column-form-field column-form-field-full">
            <label htmlFor="col-key">Key</label>
            <input
              id="col-key"
              value={column.key ?? ""}
              onChange={(e) => setField("key", e.target.value || undefined)}
              disabled={isLocked}
            />
          </div>

          <div className="column-form-field">
            <label htmlFor="col-width">Width %</label>
            <input
              id="col-width"
              value={widthValue}
              placeholder="21"
              onChange={(e) => {
                const raw = e.target.value.replace("%", "").trim();
                if (!raw) {
                  setField("width", undefined);
                  return;
                }
                const parsed = Number(raw);
                setField("width", Number.isFinite(parsed) ? parsed : undefined);
              }}
              disabled={isLocked}
            />
          </div>

          <div className="column-form-field">
            <label htmlFor="col-type">Type</label>
            <select
              id="col-type"
              value={column.type ?? ""}
              onChange={(e) =>
                setField("type", (e.target.value || undefined) as TableColumn["type"])
              }
              disabled={isLocked}
            >
              <option value="">Default</option>
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="date">Date</option>
            </select>
          </div>
        </div>

        <div className="column-align-section">
          <label>Alignment</label>
          <div className="column-align-options">
            {(["left", "center", "right"] as const).map((align) => (
              <button
                key={align}
                type="button"
                className={`column-align-btn ${
                  resolvedAlign === align ? "is-active" : ""
                }`}
                onClick={() => setField("align", align)}
                disabled={isLocked}
              >
                {align[0].toUpperCase() + align.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="column-toggle-list">
          <div className="column-toggle-row">
            <div>
              <div className="column-toggle-title">Wrap</div>
              <div className="column-toggle-text">Allow wrapping in PDF</div>
            </div>
            <div className="column-toggle-control">
              <span className="column-toggle-status">
                {column.wrap ? "Enabled" : "Disabled"}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={!!column.wrap}
                aria-label="Toggle wrap"
                className={`column-switch ${column.wrap ? "is-on" : ""}`}
                onClick={() => setField("wrap", !column.wrap)}
                disabled={isLocked}
              >
                <span className="column-switch-thumb" />
              </button>
            </div>
          </div>

          <div className="column-toggle-row">
            <div>
              <div className="column-toggle-title">Required</div>
              <div className="column-toggle-text">Mandatory for author entry</div>
            </div>
            <div className="column-toggle-control">
              <span className="column-toggle-status">
                {column.required ? "Required" : "Disabled"}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={!!column.required}
                aria-label="Toggle required"
                className={`column-switch ${column.required ? "is-on" : ""}`}
                onClick={() => setField("required", !column.required)}
                disabled={isLocked}
              >
                <span className="column-switch-thumb" />
              </button>
            </div>
          </div>
        </div>

        <div className="column-editor-actions">
          <button type="button" onClick={() => addChildColumn(column.id)} disabled={isLocked}>
            Add Child
          </button>
          <button
            type="button"
            onClick={() => {
              deleteColumn(column.id);
              selectColumn(null);
            }}
            disabled={isLocked}
          >
            Delete
          </button>
        </div>
      </div>
    </section>
  );
}
